import { defineStore } from "pinia";
import { ref } from "vue";
import type { ESPokemonSingleProduct } from "@ka-nabellinc/tcg-platform-es-schema";
import {
  type FormFields as SingleProductSearchFormFields,
  getDefaultFields,
  formValidator,
} from "@/forms/pokemon/searchSingleProduct";
import { useForm } from "@/composables/useForm";
import type { ElasticsearchGateway } from "@/interface/gateway";

interface Params {
  elasticsearchGateway: ElasticsearchGateway;
}

const PER_PAGE = 30

export const usePokemonStore = ({ elasticsearchGateway }: Params) =>
  defineStore("pokemon", () => {

    // 例外的にStoreでFormを扱うことを許容
    const {
      fields: singleProductSearchFormFields,
      clearValue,
    } = useForm(getDefaultFields(), formValidator);

    // State
    const lastSearchedSingleProductFields = ref<SingleProductSearchFormFields>()
    const showSingleProductSearchDetailMenu = ref(false)
    const currentSingleProductSearchPage = ref();
    const singleProductSearchResults = ref<ESPokemonSingleProduct[]>([]);
    const singleProductSearchTotal = ref(0);
    const singleProductSearchTotalPages = computed(() => Math.ceil(singleProductSearchTotal.value / PER_PAGE))
    const hasSingleProductSearchFormDetailFields = computed(() => {
      return (
        singleProductSearchFormFields.value.kindPokemon ||
        singleProductSearchFormFields.value.kindItem ||
        singleProductSearchFormFields.value.kindSupporter ||
        singleProductSearchFormFields.value.kindStadium ||
        singleProductSearchFormFields.value.kindPokemonTool ||
        singleProductSearchFormFields.value.kindBasicEnergy ||
        singleProductSearchFormFields.value.kindSpecialEnergy ||
        !!singleProductSearchFormFields.value.packId ||
        !!singleProductSearchFormFields.value.rarityId ||
        !!singleProductSearchFormFields.value.regulationName ||
        typeof singleProductSearchFormFields.value.sellingPriceGte === 'number' ||
        typeof singleProductSearchFormFields.value.sellingPriceLte === 'number'
      );
    });

    const searchingLastSoldSingleProduct = ref(false);
    const currentLastSoldSingleProductSearchPage = ref();
    const lastSoldSingleProductSearchTotal = ref(0);
    const lastSoldSingleProductSearchResults = ref<ESPokemonSingleProduct[]>([]);

    // Actions
    const clearPokemonSingleSearch = async () => {
      clearValue();
      await searchSingleProduct();
    };

    const searchSingleProduct = async (page: number = 1) => {
      try {
        // 最後のfetchとクエリが一致する場合は検索を行わない
        if (lastSearchedSingleProductFields.value && currentSingleProductSearchPage.value === page && JSON.stringify(lastSearchedSingleProductFields.value) === JSON.stringify(singleProductSearchFormFields.value)) {
          return
        }

        // API実行中に参照元の値が変化するためshallow copyした変数に束縛
        const searchParams = { ...singleProductSearchFormFields.value }
        const { hits, total } =
          await elasticsearchGateway.searchPokemonSingleProduct(searchParams, page)

        // 検索に利用したパラメータと最新のUI上のクエリが異なる場合はreturn(storeに値を保存しない)
        if (JSON.stringify(searchParams) !== JSON.stringify(singleProductSearchFormFields.value)) return

        lastSearchedSingleProductFields.value = {
          ...singleProductSearchFormFields.value
        }
        currentSingleProductSearchPage.value = page


        // 検索結果をstateにセット
        singleProductSearchResults.value = hits;
        singleProductSearchTotal.value = total;
      } catch (e) {
        console.log(e);
      }
    };

    const searchLastSoldSingleProduct = async (page: number = 1) => {
      try {
        searchingLastSoldSingleProduct.value = true
        const { hits, total } =
          await elasticsearchGateway.getLastSoldPokemonSingleProducts(page)

        currentLastSoldSingleProductSearchPage.value = page

        // 検索結果をstateにセット
        // pagingではなくread more での利用を想定しているので、検索結果を末尾に追加していく
        lastSoldSingleProductSearchResults.value = page === 1 ?
          hits : [...lastSoldSingleProductSearchResults.value, ...hits];
        lastSoldSingleProductSearchTotal.value = total
      } catch (e) {
        console.log(e);
      } finally {
        searchingLastSoldSingleProduct.value = false
      }

      return {
        results: lastSoldSingleProductSearchResults.value,
        total: lastSoldSingleProductSearchTotal.value
      }
    }

    return {
      searchSingleProduct,
      singleProductSearchTotal,
      singleProductSearchResults,
      singleProductSearchTotalPages,
      currentSingleProductSearchPage,

      searchLastSoldSingleProduct,
      searchingLastSoldSingleProduct,
      currentLastSoldSingleProductSearchPage,
      lastSoldSingleProductSearchTotal,
      lastSoldSingleProductSearchResults,

      // Search Form
      singleProductSearchFormFields,
      hasSingleProductSearchFormDetailFields,
      clearPokemonSingleSearch,
      showSingleProductSearchDetailMenu,
      clearValue,
    };
  });

export type PokemonStore = ReturnType<ReturnType<typeof usePokemonStore>>;
